import { Preferences } from '@3ds/common-preferences'
import { requirejsOne } from '@ds/swymkit/tools'
import type { TrackerAPI_AMD } from '@ds/swymkit/typings'
import { logger } from '@ds/swymkit/utils'
import type { RouteLocationNormalized } from 'vue-router'

import { router } from '~/router/router'
import {
  Community_Page_Regex,
  Conversation_Page_Name,
  Conversation_Page_Regex,
  My_Profile_Page_Name,
  Profile_Page_Name,
  Profile_Page_Regex,
  Rituals_Page_Name,
  Rituals_Page_Regex,
  Static_Page_Names,
  Static_Paths,
  User_Profile_Page_Regex,
} from '~/router/routes'

import { useUsersStore } from '~/entities/user/user/stores/users'

import { useContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { getPlatformId, getSwymUrl, isOnPremise } from '~/features-shared/config-manager/stores/swym-config'
import { getContentModelFromSubjectUri } from '~/features-shared/content/api/parser-contents'
import { proxifyUrl } from '~/features-shared/libs/utils/url-utils'

import { SWYM_APPNAME } from '~/features-generic/libs/config/widget-app-name'
import type {
  AllKeysOf,
  ICategoryWithPersonalDimensions,
  IContainerRequest,
  IContentPDValues,
  IContentRequest,
  IConversationPDValues,
  IExperiencePDValues,
  IHTTPRequestValues,
  ITrackerCategory,
  PageEventOptions,
  PersDims,
  PersDimsValues,
  PersVals,
  PersValsValues,
} from '~/features-generic/tracker/interfaces/tracker'
import { getUWPAppProperty } from '~/features-generic/uwp/api/uwp'

const TrackerInitComplete = ref(false)

const BRAND = '3DSwym'
const SWYM_SERVICE = 'swym'

/**
 * @returns A new instance of the provided object without undefined keys and sanitized values.
 *          Does not modify the provided object.
 * @private
 */
function sanitizeObj(obj: Record<string, unknown>, sanitizer?: (val: unknown) => unknown) {
  const sanitizedObj: Record<string, unknown> = { ...obj }

  Object.keys(obj)
    .forEach((key) => {
      const val = obj[key]
      const sanitizedVal = sanitizer ? sanitizer(val) : val

      // Remove undefined keys because TrackerAPIs will not provide PD or PV obj if
      // contains some undefined keys.
      if (sanitizedVal === undefined)
        delete sanitizedObj[key]
      else
        sanitizedObj[key] = sanitizedVal
    })

  return sanitizedObj
}

/**
 * @returns Safe Personal Dimension value type from any providing value type.
 * @private
 */
function getSafePDValue(val: unknown) {
  let safeVal: string | undefined

  if (typeof val === 'string') {
    safeVal = val
  }
  else if (typeof val === 'number' || typeof val === 'boolean') {
    safeVal = `${val}`
  }
  else if (typeof val === 'object') {
    try {
      safeVal = JSON.stringify(val)
    }
    catch (e) {
      logger.warn('Fail to getSafePDValue of:', val, e)
    }
  }

  return safeVal
}

const StaticPageHandlersWithRegex = [
  { regex: Conversation_Page_Regex, handler: () => Conversation_Page_Name },
  { regex: Community_Page_Regex, handler: () => document.title },
  { regex: Rituals_Page_Regex, handler: () => Rituals_Page_Name },
  { regex: Profile_Page_Regex, handler: (route: RouteLocationNormalized) => getPageNameFromProfilePath(route) },
]

function getPageNameFromPath(route: RouteLocationNormalized) {
  const fullPath = route.fullPath
  const path = route.path
  let pageName = ''

  if (Static_Paths[fullPath]) {
    pageName = Static_Page_Names[Static_Paths[fullPath]]
  }
  else if (router.getQuery(route, 'media') || router.getQuery(route, 'content')) { // content detailed view
  // note: on media detailed view, document title is that of the community/conversation/WN, not of the media
    pageName = document.title
  }
  else {
    const pageObj = StaticPageHandlersWithRegex.find(el => path.match(el.regex))
    pageName = pageObj ? pageObj.handler(route) : ''
  }

  // logger.log('pageTitle', pageName, route)
  return `${BRAND} ${pageName}`
}

function getPageNameFromProfilePath(route: RouteLocationNormalized) {
  let pageName = ''
  const path = route.path
  const storeUsers = useUsersStore()
  const currentUserLogin = storeUsers.currentUser?.login

  if (currentUserLogin && path.match(User_Profile_Page_Regex(currentUserLogin)))
    pageName = My_Profile_Page_Name
  else
    pageName = Profile_Page_Name

  return pageName
}

function getAppName() {
  // Check App Id from http://dmuarchi/archi/searchpack_app.html
  return SWYM_APPNAME // TODO Q61 to extend for 3DD, My3DExperience, etc
}

/**
 * @returns Built Personal Dimensions object with the provided Content PD Values.
 * @private
 */
function buildContentPDObj(pdValues: Partial<IContentPDValues>) {
  const pdObj: PersDims = {}

  pdObj.pd1 = pdValues.component
  pdObj.pd2 = pdValues.contentURI?.split(':')
    .pop() // SHOULD BE THE ID !!!
  pdObj.pd3 = pdValues.contentType
  pdObj.pd4 = pdValues.containerURI?.split(':')
    .pop() // SHOULD BE THE ID !!!
  pdObj.pd5 = pdValues.containerType
  pdObj.pd6 = pdValues.context
  pdObj.pd7 = pdValues.type
  pdObj.pd8 = pdValues.mediaType
  pdObj.pd9 = pdValues.subContentType
  pdObj.pd10 = pdValues.subContentURI?.split(':')
    .pop() // SHOULD BE THE ID !!!
  pdObj.pd11 = pdValues.error
  pdObj.pd12 = pdValues.extension

  return pdObj
}
/**
 * @returns Built Personal Dimensions object with the provided conversation PD Values.
 * @private
 */
function buildConversationPDObj(pdValues: IConversationPDValues) {
  const pdObj: PersDims = {}

  pdObj.pd18 = pdValues.conversationURI
  pdObj.pd19 = pdValues.markId
  pdObj.pd20 = pdValues.lastMessageId

  return pdObj
}

function buildExperiencePDObj(pdValues: Partial<IExperiencePDValues>) {
  const pdObj: PersDims = {}

  pdObj.pd2 = pdValues.facetURI // contentURI
  pdObj.pd3 = pdValues.experienceName // contentType

  pdObj.pd4 = pdValues.containerURI?.split(':') // containerURI
    .pop() // SHOULD BE THE ID !!!
  pdObj.pd5 = pdValues.containerType // containerType

  pdObj.pd7 = pdValues.appId // type

  pdObj.pd10 = pdValues.resourceId // subContentURI

  pdObj.pd11 = pdValues.error // error

  return pdObj
}

/**
 * @returns Built Personal Dimensions object with the provided HTTPRequest OR
 *          ContainerRequest or ContentRequest PD Values.
 * @private
 */
function buildHTTPRequestPDObj(pdValues: IHTTPRequestValues | IContainerRequest | IContentRequest) {
  const pdObj: PersDims = {}

  pdObj.pd1 = pdValues.api
  pdObj.pd2 = pdValues.method
  pdObj.pd3 = pdValues.crud

  if ('containerType' in pdValues) {
    pdObj.pd5 = pdValues.containerType
    pdObj.pd6 = pdValues.containerURI

    if ('contentType' in pdValues) {
      pdObj.pd7 = pdValues.contentType
      pdObj.pd8 = pdValues.contentURI
      pdObj.pd9 = pdValues.mediaType
    }
  }

  pdObj.pd10 = pdValues.xRequestId

  if (pdValues.responseError)
    pdObj.pd11 = pdValues.responseError

  return pdObj
}

/**
 * @returns Built Personal Values object with the provided HTTPRequest
 * @private
 */
function buildHTTPRequestPVObj(pvValues: IHTTPRequestValues) {
  const pvObj: PersVals = {}

  pvObj.pv4 = pvValues.responseStatusCode
  pvObj.pv12 = pvValues.time

  if (pvValues.nbElements)
    pvObj.pv5 = pvValues.nbElements

  return pvObj
}

/**
 * @returns Built Personal Dimensions object with the provided PD Values.
 * @private
 */
function buildPDObj(pdValues: PersDimsValues) {
  let pdObj: PersDims = {}

  if (typeof pdValues === 'object') {
    if ('experienceName' in pdValues) {
      pdObj = buildExperiencePDObj(pdValues)
    }
    else if ('component' in pdValues && pdValues.component) {
      pdObj = buildContentPDObj(pdValues)
    }
    else if ('api' in pdValues && pdValues.api) {
      pdObj = buildHTTPRequestPDObj(pdValues)
    }
    else {
      // unknown format
      logger.warn('Unknown format', pdValues)
    }

    // If the format is directly the pd1, pd2, pd3 format.
    // (to be avoided and prefer to use above formats).
    const _pd = Object.fromEntries(Object.entries(pdValues)
      .filter(([key]) => key.startsWith('pd')))

    pdObj = { ...pdObj, ..._pd }

    if ('markId' in pdValues)
      pdObj = { ...pdObj, ...buildConversationPDObj(pdValues) }
  }

  return pdObj
}

/**
 * @returns Built Personal Values object with the provided PV Values.
 * @private
 */
function buildPVObj(pvValues: PersValsValues) {
  let pvObj: PersVals = {}

  if (typeof pvValues === 'object') {
    if ('responseStatusCode' in pvValues) {
      pvObj = buildHTTPRequestPVObj(pvValues)
    }
    // If the format is directly the pv1, pv2, pv3 format.
    // (to be avoided and prefer to use above formats).
    else if ('pv1' in pvValues) {
      pvObj = pvValues
    }
    else {
      // unknown format
      logger.warn('Unknown format', pvValues)
    }
  }

  return pvObj
}

/**
 * @returns Build Personal Dimensions object with mandatory PD values.
 * @private
 */
function buildMandatoryPDObj() {
  const { contextKey } = useContextManager()
  const currentRoute = router.currentRoute.value
  // Building route for filtered routes
  // (NOTE: that not all routes need to be changed, route filtered through id have the content_type directly added to the meta.name)
  // adding a fall back if developer don't add meta.name in the route tag of a page
  let buildedRouteName = ''
  if (currentRoute.meta.name) {
    buildedRouteName = currentRoute.meta.name?.toString()
    // @ts-expect-error ignore this error, this is safe
      .replace('{content_type}', currentRoute.params.content_type || '')
  }
  else {
    buildedRouteName = currentRoute.name?.toString() || ''
    logger.error(`meta.name of ${buildedRouteName} route does not exist please add a meta.name to the route`)
  }

  const persDim: PersDims = {
    pd14: 'V3',
    pd15: currentRoute.fullPath,
    pd16: contextKey.value,
    pd17: buildedRouteName,
  }

  // we ony send pd13 if thread_id as a value
  if ('thread_id' in currentRoute.params)
    persDim.pd13 = currentRoute.params.thread_id! as string

  return persDim
}

/**
 * @returns Built Personal Values object with mandatory PV values.
 * @private
 */
function buildMandatoryPVObj() {
  // TODO Q61: add version info as well (for ex. for 1.19.9, use pv12=1, pv13=19, pv14=9)
  const persVal: PersVals = {
    pv3: 3,
  }

  return persVal
}

export function getTrackerOptionsForPageView(route: RouteLocationNormalized) {
  const contentURI = router.getQuery(route, 'media') || router.getQuery(route, 'content')
  return {
    appID: getAppName(),
    pageURL: route.fullPath,
    pageTitle: getPageNameFromPath(route),
    tenant: getPlatformId(),
    persDim: sanitizeObj({
      ...buildContentPDObj({
        containerType: route.name?.toString()
          .startsWith('dm')
          ? 'conversation'
          : 'community',
        containerURI: ('thread_id' in route.params && (route.params.thread_id as string)) || '',
        contentType: contentURI
          ? getContentModelFromSubjectUri(contentURI)
          : undefined,
        contentURI,
      }),
      ...buildMandatoryPDObj(),
    }, getSafePDValue),
    persVal: sanitizeObj(buildMandatoryPVObj()),
  }
}

export function getTrackerOptionsForPageEvent<
  T extends ITrackerCategory,
  U extends AllKeysOf<ICategoryWithPersonalDimensions[T]>,
  V extends ICategoryWithPersonalDimensions[T][U],
>(component: string, options: PageEventOptions<T, U, V>) {
  const { contextKey } = useContextManager()
  const { category, label, event, pd, pv } = options

  const tenant = getPlatformId()
  const route = router.currentRoute.value
  const contentURI = router.getQuery(route, 'media') || router.getQuery(route, 'content')
  return {
    eventCategory: `swym.${category}`,
    eventAction: event,
    eventLabel: label,
    appID: getAppName(),
    tenant,
    persDim: sanitizeObj({
      ...buildPDObj({
        component,
        context: contextKey.value,
        containerType: (route.name?.toString()
          .startsWith('dm') || route.name?.toString()
          .startsWith('aura'))
          ? 'conversation'
          : 'community',
        containerURI: ('thread_id' in route.params && (route.params.thread_id as string)) || '',
        contentType: contentURI
          ? getContentModelFromSubjectUri(contentURI)
          : undefined,
        contentURI,
        ...pd as PersDimsValues,
      }),
      ...buildMandatoryPDObj(),
    }, getSafePDValue),
    persVal: sanitizeObj({
      ...buildPVObj(pv as PersValsValues),
      ...buildMandatoryPVObj(),
    }),
  }
}

const isLogActivated = globalThis?.location.search.includes('debuglog=tracker')

export function useTracker(component: string) {
  const trackPageView = async (route: RouteLocationNormalized) => {
    const TrackerAPI = await requirejsOne<TrackerAPI_AMD>('DS/WebTrackerUsage/TrackerAPI')
    await until(TrackerInitComplete)
      .toBe(true)

    const optionsToSend = getTrackerOptionsForPageView(route)

    if (isLogActivated)
      logger.debug('trackPageView', optionsToSend)

    TrackerAPI.trackPageView(optionsToSend)
  }

  /**
   * Track a page event action (see {@link EventAction} list)
   * @param options - {@link PageEventOptions}.
   * @returns Empty promise.
   * @example
   * trackPageEvent({
   *   category: 'ContentMenu',
   *   label: 'showHistoryItem',
   *   event: 'click',
   *   pd: {
   *     component: 'useContentMenu',
   *     contentType: content.value.model,
   *     contentURI: content.value.subject_uri,
   *     containerType: content.value.community_type !== 'dm' ? 'community' : 'conversation',
   *     containerURI: content.value.community_subject_uri,
   *     context: content.value.community_type !== 'dm' ? 'COMMUNITY' : 'DM',
   *   }
   * })
   */

  async function trackPageEvent<
    T extends ITrackerCategory,
    U extends AllKeysOf<ICategoryWithPersonalDimensions[T]>,
    V extends ICategoryWithPersonalDimensions[T][U],
  >(options: PageEventOptions<T, U, V>): Promise<void> {
    // Wait until the tracker is initialized. It includes the fact that the tenant had been fetched
    // so it avoid infinite loop on /api/index/options
    await until(TrackerInitComplete)
      .toBe(true)

    try {
      const TrackerAPI = await requirejsOne<TrackerAPI_AMD>('DS/WebTrackerUsage/TrackerAPI')
      const optionsToSend = getTrackerOptionsForPageEvent(component, options)

      if (isLogActivated)
        logger.debug('trackPageEvent', optionsToSend)

      TrackerAPI.trackPageEvent(optionsToSend)
    }
    catch (err) {
      logger.error('Error in trackPageEvent', err)
    }
  }

  const initTracker = async () => {
    const UsageEnabledStatus = await getUWPAppProperty('app.usage.enable')
    const isUsageDisabled = UsageEnabledStatus === 'false' || isOnPremise()

    // Usage are disabled, stop the setup
    if (isUsageDisabled)
      return

    const storeUsers = useUsersStore()
    const UsageManager = await requirejsOne<TrackerAPI_AMD>('DS/WebTrackerUsage/UsageManager')
    const TrackerAPI = await requirejsOne<TrackerAPI_AMD>('DS/WebTrackerUsage/TrackerAPI')

    if (Preferences.isNativeApp()) {
      const { seed, hostname, service, them } = (await Preferences.getTrackerData())

      UsageManager.init({
        uploadPath: proxifyUrl(`${getSwymUrl()}/upload`, {
          method: 'POST',
        }),
        service: service || SWYM_SERVICE,
        user: {
          login: storeUsers.currentUser?.login || '',
          // uuid:storeUsers.currentUser?.uid || '', //TODO: FD01
        },
        seed,
        hostname,
        them,
      })
    }
    else {
      UsageManager.init({
        uploadPath: `${getSwymUrl()}/upload`,
        service: SWYM_SERVICE,
        user: {
          login: storeUsers.currentUser?.login || '',
          // uuid:storeUsers.currentUser?.uid || '', //TODO: FD01
        },
      })
    }
    // add the PVs for the session with the method registerData
    TrackerAPI.registerData(sanitizeObj(buildMandatoryPVObj()))
    TrackerInitComplete.value = true
  }

  function setInitTrackerComplete() {
    // for widgets tracker is already initialized by 3DD
    // so init is not needed
    // just update the ref so tracking can start
    TrackerInitComplete.value = true
  }

  function initPageAnalytics() {
    router.afterEach(trackPageView)
    trackPageView(router.currentRoute.value)
  }

  return {
    trackPageEvent,
    trackPageView,
    setInitTrackerComplete,
    initPageAnalytics,
    initTracker,
  }
}
